/*
Routing manager of the application.

Navigation guards are used and evaluated on every change of selectors that are used in the guards. They
are particularly useful for redirecting users to the correct page based on their global state.

Then, there are some redirects that are used to ensure that the user is always on the correct page
when the application is loaded, so no matter where the user is, they will be redirected to the
correct page if they have an active lobby or game, or others use cases.
*/

import { Fragment, useRef, useEffect } from "react"
import { Routes, Route, useLocation, Navigate, useParams, useNavigate } from "react-router-dom"
import { AnimatePresence, motion } from "framer-motion"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { selectBasicTrainingPending, selectIsAuthenticated } from "src/store/user"
import { arrivedHome, selectLobby, selectSentHome } from "src/store/lobby"
import { enterGame, selectGame, selectGames } from "src/store/game"
import { selectUserId } from "src/store/user"
import { logMessage } from "src/logging"
import { basicTrainingPath, shouldStartBasicTraining } from "src/utils/training"
import { routePathsFor } from "src/utils/languagePaths"
import LanguageInPath from "src/components/LanguageInPath"
import DocumentTitle from "src/components/DocumentTitle"
import { TRAINING_GAME_ID } from "src/tutorial/scenario"

import Home from "src/views/Home"
import HowToPlay from "src/views/HowToPlay"
import Tutorial from "src/views/Tutorial"
import Legal from "src/views/Legal"
import Lobby from "src/views/Lobby"
import LobbyInvite from "src/views/LobbyInvite"
import Game from "src/views/Game"
import Replay from "src/views/Replay"
import Ranking from "src/views/Ranking"
import ResetPassword from "src/views/ResetPassword"

/* A public page answers at one address per language — `/how-to-play` and `/es/how-to-play` are
   the same route (`utils/languagePaths`): the server writes a Spanish head for the second so a
   crawler files it as the Spanish manual, and `LanguageInPath` below keeps the address and the
   player's language agreeing. Only the public pages: a lobby or a game has no Spanish address. */
const publicRoutes = (path, element) =>
  routePathsFor(path).map((routePath) => <Route key={routePath} path={routePath} element={element} />)

const TransitionComponent = ({ children, ...props }) => (
  <motion.div
    {...props}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.5, ease: "easeInOut" }}
  >
    {children}
  </motion.div>
)

const IsAuthenticatedNavigationGuard = ({ children, redirectPath = "/" }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated)

  const hasDispatched = useRef(false)
  useEffect(() => {
    if (!isAuthenticated && !hasDispatched.current) {
      hasDispatched.current = true
      logMessage({ text: "You have no power here!😡", type: "error" })
    }
  }, [isAuthenticated])

  if (!isAuthenticated) {
    return <Navigate to={redirectPath} replace />
  }
  return children
}

const ExistingRouteNavigationGuard = ({ children }) => {
  const hasDispatched = useRef(false)

  useEffect(() => {
    if (!hasDispatched.current) {
      hasDispatched.current = true
      logMessage({ text: "Where are you going?😒", type: "error" })
    }
  }, [])
  return children
}

/* `/lobby/:number` for somebody who is not in it yet — which is every person an invite link is
   sent to. It used to be two guards, "signed in" and "already a member", and both answered a
   stranger by bouncing them to the home page with a toast. That made the invite link work only
   for the players who already had no use for it.

   Being a member is now the *reason to render the lobby*, not the price of admission. Anyone else
   gets the invite screen, which signs them in, asks for the password if the lobby wants one, and
   joins them — after which they are a member and this renders the lobby. */
const LobbyRoute = () => {
  const { number } = useParams()
  const currentLobby = useSelector(selectLobby)
  const isMember = Boolean(currentLobby) && currentLobby.number.toString() === number

  /* Whether this mount has rendered the lobby as a member. Losing the room afterwards is not a
     stranger arriving with an invite link — it is the player *leaving*: LEAVE resets the lobby
     slice and navigates home, and `AnimatePresence` keeps this route mounted through the exit
     animation, re-rendering it against the emptied store. It used to answer that re-render with
     the invite screen, which joins a signed-in visitor on mount: the player was re-seated in the
     room they had just walked out of, the lobby pin above sent them straight back into it, and
     LEAVE never worked again while anybody else was in the room (alone, the room was deleted
     under the re-join and the refusal let them out — which is why it read as "only when
     somebody else is on the team"; owner, 2026-09-24). The same re-render follows a game start,
     where it re-joined a lobby the start had just deleted. Keyed by the number, so a jump from
     one lobby's address to another is a fresh arrival. */
  const seen = useRef({ number: null, wasMember: false })
  if (seen.current.number !== number) seen.current = { number, wasMember: isMember }
  else if (isMember) seen.current.wasMember = true
  const leaving = !isMember && seen.current.wasMember

  /* Put out by the room (the host removed this account, or another tab gave the seat up): no
     LEAVE and no game start is navigating, so this route does — else the address kept a black
     screen and a reload joined the room again (owner, 2026-10-16). */
  const sentHome = useSelector(selectSentHome)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  useEffect(() => {
    if (!sentHome) return
    dispatch(arrivedHome())
    navigate("/")
  }, [sentHome, dispatch, navigate])

  if (isMember) return <Lobby />
  // Leaving: the app is already navigating; render nothing and let it.
  if (leaving) return null
  return <LobbyInvite number={number} />
}

/* `/game/:gameId` renders the game the URL names. The store holds every live game of the
   account (owner, 2026-09-22: a player sits in several at once), so the guard picks the named one
   into the viewed slot (`enterGame`) and renders nothing until the store says it is there — the
   view reads `selectGame` and would mount on whichever game was viewed last. A game in neither
   the viewed slot nor the list is somebody else's. */
const ExistingGameNavigationGuard = ({ children }) => {
  const { gameId } = useParams()
  const dispatch = useDispatch()
  const currentGame = useSelector(selectGame)
  const knownGames = useSelector(selectGames)
  const isViewed = Boolean(currentGame) && currentGame.id.toString() === gameId
  const isKnown = knownGames.some((game) => game.id.toString() === gameId)
  const canAccessGame = isViewed || isKnown
  /* Whether this URL's game has been viewed under this guard. Losing the view later is not an
     intruder and not a game to re-enter — it is the player leaving: `goHome`, GameOverModal's
     exits and the eliminated banner dispatch leaveGame() and navigate away, and AnimatePresence
     keeps this route mounted through the exit animation. The guard used to re-render against
     the emptied store, shout "You don't belong to this game!😡" at somebody who had just
     finished playing it (Q33), and issue a Navigate home that could hijack the in-flight
     navigation to the replay (Q1) — and with the game still on the shelf it would now re-enter
     the game the player has just left. Keyed by the URL, so a jump from one game straight to
     another is a fresh arrival and not a departure. */
  const viewed = useRef({ gameId: null, wasViewed: false })
  if (viewed.current.gameId !== gameId) viewed.current = { gameId, wasViewed: isViewed }
  else if (isViewed) viewed.current.wasViewed = true
  const leaving = !isViewed && viewed.current.wasViewed
  useEffect(() => {
    if (!isViewed && isKnown && !leaving) dispatch(enterGame(gameId))
  }, [dispatch, isViewed, isKnown, leaving, gameId])
  const hasDispatched = useRef(false)

  useEffect(() => {
    if (!canAccessGame && !leaving && !hasDispatched.current) {
      hasDispatched.current = true
      logMessage({ text: "You don't belong to this game!😡", type: "error" })
    }
  }, [canAccessGame, leaving])

  // Leaving: the app is already navigating; render nothing and let it.
  if (leaving) return null
  // Arrived without access: a wrong or stale URL — say so and go home.
  if (!canAccessGame) return <Navigate to="/" replace />
  // Known but not yet viewed: the effect above is picking it; nothing to draw until it has.
  if (!isViewed) return null
  // Keyed by the game, so a jump between two game URLs remounts the view — its socket is opened
  // once per mount, on the game it mounted with.
  return <Fragment key={gameId}>{children}</Fragment>
}

const AppRoutes = () => {
  const location = useLocation()
  const { i18n } = useTranslation()
  const activeLobby = useSelector(selectLobby)
  const activeGame = useSelector(selectGame)
  const userId = useSelector(selectUserId)
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const basicTrainingPending = useSelector(selectBasicTrainingPending)

  // Redirect to the active lobby if it exists and we are not already on that page
  if (activeLobby && location.pathname !== `/lobby/${activeLobby.number}`) {
    return <Navigate to={`/lobby/${activeLobby.number}`} replace />
  }

  /* A live game no longer pins the player to it (owner, 2026-09-22: "the games that are 1 day
     or 1 week long should not block the whole profile ... we have to be able to go back to the
     home from that game"). Until then anybody holding a seat in an unfinished game was redirected
     into it from every other route, on every reload — right for a minute a round, and the whole
     account held hostage at a day a round. The home page lists the live games instead
     (`LiveGames`), the game's top bar has a door home, and a player who wants to be in the game
     opens it. What that pin was also doing — walking a player into the game the moment the lobby
     starts it — is the clause below. */

  /* A new account is walked into basic training once (owner's ask, 2026-09-14): the server
     keeps `basic_training_seen_at` null until the tutorial has opened for the account, and the
     view marks it the moment it does. Below the lobby and game pins on purpose — an account that
     already holds a seat somewhere belongs there — and `utils/training` says which other routes
     are left alone (an invite link, a password reset). Both sign-up doors land here the same way,
     since each ends in `loadUserData` and `/users/me/` is what carries the flag.

     At the player's own language's address (`/es/tutorial` for Spanish), not the bare one: a bare
     `/tutorial` is moved by `LanguageInPath` a beat later, and that second navigation remounted
     the tutorial under `AnimatePresence` on top of the one still fading out — `utils/training`
     has the story. */
  if (shouldStartBasicTraining({ isAuthenticated, pending: basicTrainingPending, pathname: location.pathname })) {
    return <Navigate to={basicTrainingPath(i18n.resolvedLanguage)} replace />
  }

  /* When the lobby somebody is standing in turns into a game, they are *sent in* once — seat
     or spectator — or the start leaves them on a lobby route that no longer exists ("that room
     no longer exists" was what a spectator-host saw after pressing start, and since the pin
     above went it is what everybody would see). The lobby's `user_started_game` puts the game in
     the viewed slot and drops the room, which is exactly the state this reads: a live game
     viewed, no lobby, and a lobby path under the feet. From anywhere else — home, the game-over
     screen — nobody is sent anywhere.

     It yields to an active lobby. The lobby pin above sends anybody with a lobby to it; somebody
     viewing a live game who then opens a *new* lobby holds both, and without this clause the two
     redirects hand the same person back and forth for ever ("Maximum update depth exceeded", a
     black screen). */
  const isInGame = Boolean(
    activeGame?.players?.some((seat) => seat.user.id === userId) ||
      activeGame?.spectators?.some((watcher) => watcher.id === userId)
  )
  if (
    isInGame &&
    !activeLobby &&
    activeGame.status !== "finished" &&
    String(activeGame.id) !== String(TRAINING_GAME_ID) &&
    location.pathname.startsWith("/lobby/")
  ) {
    return <Navigate to={`/game/${activeGame.id}`} replace />
  }

  /* Deliberately *not* `<AnimatePresence mode="wait">`. That mode holds the incoming route until
     the outgoing one's exit animation reports itself finished — so anything that stalls that
     animation leaves the app showing a page the URL no longer names, with no way forward. It is
     exactly the shape the owner reported on 2026-09-04 ("the URL changes but the page keeps
     showing the same"): a render loop in the incoming view wedged React mid-transition, and a
     stalled exit is indistinguishable from the app being broken.

     The loop is fixed (see `Canvas`'s hook refs), but the fragility is the part worth removing: a
     page transition must not be able to strand a navigation. Without the mode the two pages
     cross-fade over the same 500ms instead of fading out and then in, and anything that freezes
     animations — a backgrounded tab, a slow frame — costs a fade rather than the route. */
  return (
    <AnimatePresence>
      <LanguageInPath />
      {/* Keyed: AnimatePresence tells its children apart by key, and two keyless ones are one. */}
      <DocumentTitle key="document-title" />
      <Routes location={location} key={location.pathname}>
        {publicRoutes(
          "/",
          <TransitionComponent key="home">
            <Home />
          </TransitionComponent>
        )}

        {/* The manual. Public on purpose: how the game works is the pitch, not a member perk. */}
        {publicRoutes(
          "/how-to-play",
          <TransitionComponent key="how-to-play">
            <HowToPlay />
          </TransitionComponent>
        )}

        {/* Basic training. Public for the same reason the manual is, and with the same shape of
            content: the scenario is scripted client-side, so it needs no account, no lobby and no
            socket — see `src/tutorial/scenario.js`. */}
        {publicRoutes(
          "/tutorial",
          <TransitionComponent key="tutorial">
            <Tutorial />
          </TransitionComponent>
        )}

        {/* The privacy policy and the terms: public, and in both languages like the manual. The text
            is gamekit's (`GET /api/legal/<doc>/`). */}
        {publicRoutes(
          "/privacy",
          <TransitionComponent key="privacy">
            <Legal doc="privacy" />
          </TransitionComponent>
        )}
        {publicRoutes(
          "/terms",
          <TransitionComponent key="terms">
            <Legal doc="terms" />
          </TransitionComponent>
        )}

        <Route
          path="/lobby/:number"
          element={
            <TransitionComponent key="lobby">
              <LobbyRoute />
            </TransitionComponent>
          }
        />

        <Route
          path="/game/:gameId"
          element={
            <IsAuthenticatedNavigationGuard redirectPath="/">
              <ExistingGameNavigationGuard>
                <TransitionComponent key="game">
                  <Game />
                </TransitionComponent>
              </ExistingGameNavigationGuard>
            </IsAuthenticatedNavigationGuard>
          }
        />

        {/* A finished game, watched from above. No ExistingGameNavigationGuard: a finished game
            is deliberately not in the bootstrap list, so the view fetches its own history. The
            active-game pin above still applies — a player in a live game belongs in it. */}
        <Route
          path="/replay/:gameId"
          element={
            <IsAuthenticatedNavigationGuard redirectPath="/">
              <TransitionComponent key="replay">
                <Replay />
              </TransitionComponent>
            </IsAuthenticatedNavigationGuard>
          }
        />

        {/* The hall of fame's full list. Open to anybody, like the podium on the home page it
            comes from; MY POSITION appears only for a signed-in player. Not a public page: the
            server serves it `noindex` (`seo.PRIVATE_PREFIXES`). */}
        <Route
          path="/ranking"
          element={
            <TransitionComponent key="ranking">
              <Ranking />
            </TransitionComponent>
          }
        />

        <Route
          path="/reset-password/:uid/:token"
          element={
            <TransitionComponent key="reset-password">
              <ResetPassword />
            </TransitionComponent>
          }
        />

        {/* Catch-All Redirect to Home */}
        <Route
          path="*"
          element={
            <ExistingRouteNavigationGuard>
              <TransitionComponent key="default">
                <Navigate to="/" replace />
              </TransitionComponent>
            </ExistingRouteNavigationGuard>
          }
        />
      </Routes>
    </AnimatePresence>
  )
}

export default AppRoutes
